<template>
  <div class="instructor-filter" ref="root">
    <div class="instructor-filter-anchor">
      <button type="button" class="instructor-filter-button" :disabled="disabled || options.length === 0"
        :title="options.length === 0 ? 'No tasks have been assigned to the selected trainees yet' : ''"
        @click="open = !open">
        Select Instructors
        <span class="instructor-filter-caret" :class="{ open }">▾</span>
      </button>

      <div v-if="open" class="instructor-filter-panel">
        <div class="instructor-filter-panel-header">
          <span>Show tasks assigned by</span>
          <button type="button" class="instructor-filter-link" :disabled="!modelValue.length" @click="clearAll">
            Clear
          </button>
        </div>
        <label v-for="option in options" :key="option.user_id" class="instructor-filter-option">
          <input type="checkbox" :checked="modelValue.includes(option.user_id)" @change="toggle(option.user_id)" />
          <span class="instructor-filter-name">
            {{ option.name }}<span v-if="option.user_id === currentUserId" class="instructor-filter-you"> (you)</span>
          </span>
          <span class="instructor-filter-count" :title="`${option.count} task assignment(s)`">{{ option.count }}</span>
        </label>
      </div>
    </div>

    <div class="chip-container">
      <span v-if="!modelValue.length" class="instructor-filter-hint">
        All instructors — select one or more to only show tasks they assigned
      </span>
      <span v-for="option in selectedOptions" :key="option.user_id" class="chip">
        {{ option.name }}
        <font-awesome-icon class="chip-close" :icon="['fas', 'times']" title="Remove instructor from the filter"
          @click="toggle(option.user_id)" />
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InstructorFilter',
  props: {
    // [{ user_id, name, count }]
    options: { type: Array, required: true },
    modelValue: { type: Array, required: true },
    currentUserId: { type: Number, default: null },
    disabled: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      open: false,
    };
  },
  computed: {
    selectedOptions() {
      return this.options.filter(option => this.modelValue.includes(option.user_id));
    },
  },
  watch: {
    // Drop instructors that no longer apply, e.g. after the trainee group changes
    options(newOptions) {
      const available = new Set(newOptions.map(option => option.user_id));
      const kept = this.modelValue.filter(id => available.has(id));
      if (kept.length !== this.modelValue.length) {
        this.$emit('update:modelValue', kept);
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
  methods: {
    toggle(userId) {
      const next = this.modelValue.includes(userId)
        ? this.modelValue.filter(id => id !== userId)
        : [...this.modelValue, userId];
      this.$emit('update:modelValue', next);
    },
    clearAll() {
      this.$emit('update:modelValue', []);
    },
    handleOutsideClick(event) {
      if (this.open && this.$refs.root && !this.$refs.root.contains(event.target)) {
        this.open = false;
      }
    },
  },
};
</script>

<style scoped>
.instructor-filter {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
}

.instructor-filter-anchor {
  position: relative;
}

.instructor-filter-button {
  border-radius: 5px;
  min-width: 170px;
  height: 30px;
  background-color: #463333;
  color: white;
  border: 1px solid #200d0d;
  margin: 10px 15px;
  padding: 0 10px;
  display: inline-block;
}

.instructor-filter-button:hover:not(:disabled) {
  background-color: #2b1616;
  transform: scale(1.05);
  transition: 0.3s;
}

.instructor-filter-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.instructor-filter-caret {
  display: inline-block;
  margin-left: 6px;
  transition: transform 0.2s ease;
}

.instructor-filter-caret.open {
  transform: rotate(180deg);
}

.instructor-filter-panel {
  position: absolute;
  top: 44px;
  left: 15px;
  z-index: 50;
  min-width: 280px;
  max-height: 320px;
  overflow-y: auto;
  padding: 6px;
  background: #fff;
  border: 1px solid #d0d5dc;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.15);
}

.instructor-filter-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px 8px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.instructor-filter-link {
  border: none;
  background: none;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  color: #007bff;
  cursor: pointer;
}

.instructor-filter-link:disabled {
  color: #b0b7c3;
  cursor: default;
}

.instructor-filter-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 8px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.instructor-filter-option:hover {
  background-color: #f1f4f8;
}

.instructor-filter-name {
  flex: 1;
}

.instructor-filter-you {
  color: #6b7280;
}

.instructor-filter-count {
  min-width: 26px;
  padding: 1px 7px;
  border-radius: 10px;
  background-color: #eef2f7;
  color: #374151;
  font-size: 12px;
  text-align: center;
}

.chip-container {
  margin-top: 10px;
}

.instructor-filter-hint {
  display: inline-block;
  padding: 5px 0;
  font-size: 14px;
  font-style: italic;
  color: #888;
}

.chip {
  display: inline-block;
  padding: 5px 10px;
  margin: 5px;
  background-color: #e0f3ff;
  border-radius: 20px;
  font-size: 14px;
  position: relative;
}

.chip-close {
  margin-left: 8px;
  cursor: pointer;
  color: #888;
}
</style>
